import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ isOpen, onClose, filters, onFiltersChange, isMobile = false }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    brand: true,
    price: true,
    dietary: true,
    rating: true
  });

  const categories = [
    { id: 'fresh-fruits', name: 'Fresh Fruits', count: 156 },
    { id: 'vegetables', name: 'Vegetables', count: 234 },
    { id: 'dairy', name: 'Dairy & Eggs', count: 89 },
    { id: 'meat', name: 'Meat & Seafood', count: 67 },
    { id: 'bakery', name: 'Bakery', count: 45 },
    { id: 'beverages', name: 'Beverages', count: 123 }
  ];

  const brands = [
    { id: 'organic-valley', name: 'Organic Valley', count: 45 },
    { id: 'fresh-farm', name: 'Fresh Farm', count: 67 },
    { id: 'green-choice', name: 'Green Choice', count: 34 },
    { id: 'nature-best', name: 'Nature\'s Best', count: 56 },
    { id: 'farm-fresh', name: 'Farm Fresh', count: 23 }
  ];

  const dietaryOptions = [
    { id: 'organic', name: 'Organic', count: 89 },
    { id: 'gluten-free', name: 'Gluten Free', count: 45 },
    { id: 'vegan', name: 'Vegan', count: 67 },
    { id: 'non-gmo', name: 'Non-GMO', count: 123 },
    { id: 'keto', name: 'Keto Friendly', count: 34 }
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFilterChange = (type, value, checked) => {
    setLocalFilters(prev => ({
      ...prev,
      [type]: checked 
        ? [...(prev[type] || []), value]
        : (prev[type] || []).filter(item => item !== value)
    }));
  };

  const handlePriceChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      priceRange: {
        ...prev.priceRange,
        [field]: value
      }
    }));
  };

  const handleRatingChange = (rating) => {
    setLocalFilters(prev => ({
      ...prev,
      minRating: prev.minRating === rating ? 0 : rating
    }));
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
    if (isMobile) {
      onClose();
    }
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      categories: [],
      brands: [],
      dietary: [],
      priceRange: { min: '', max: '' },
      minRating: 0
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const renderRatingStars = (rating, isSelected) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon 
          key={i} 
          name="Star" 
          size={16} 
          className={`${i <= rating ? 'text-warning fill-current' : 'text-border'}`}
        />
      );
    }
    return (
      <div className="flex items-center space-x-1">
        <div className="flex items-center space-x-0.5">{stars}</div>
        <span className="text-sm text-text-secondary">& up</span>
      </div>
    );
  };

  const FilterSection = ({ title, sectionKey, children }) => (
    <div className="border-b border-border pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full py-2 text-left"
      >
        <h3 className="font-heading font-heading-semibold text-text-primary">
          {title}
        </h3>
        <Icon 
          name={expandedSections[sectionKey] ? "ChevronUp" : "ChevronDown"} 
          size={20} 
          className="text-text-secondary"
        />
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-3 space-y-2">
          {children}
        </div>
      )}
    </div>
  );

  const content = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-heading-semibold text-lg text-text-primary">
          Filters
        </h2>
        <div className="flex items-center space-x-2">
          <Button
            variant="text"
            onClick={clearAllFilters}
            className="text-sm"
          >
            Clear All
          </Button>
          {isMobile && (
            <Button
              variant="ghost"
              onClick={onClose}
              className="p-2"
              iconName="X"
              iconSize={20}
            />
          )}
        </div>
      </div>

      {/* Categories */}
      <FilterSection title="Categories" sectionKey="category">
        {categories.map((category) => (
          <label key={category.id} className="flex items-center space-x-3 cursor-pointer">
            <Input
              type="checkbox"
              checked={localFilters.categories?.includes(category.id) || false}
              onChange={(e) => handleFilterChange('categories', category.id, e.target.checked)}
              className="w-4 h-4"
            />
            <span className="flex-1 text-sm text-text-primary">{category.name}</span>
            <span className="text-xs text-text-secondary">({category.count})</span>
          </label>
        ))}
      </FilterSection>

      {/* Brands */}
      <FilterSection title="Brands" sectionKey="brand">
        {brands.map((brand) => (
          <label key={brand.id} className="flex items-center space-x-3 cursor-pointer">
            <Input
              type="checkbox"
              checked={localFilters.brands?.includes(brand.id) || false}
              onChange={(e) => handleFilterChange('brands', brand.id, e.target.checked)}
              className="w-4 h-4"
            />
            <span className="flex-1 text-sm text-text-primary">{brand.name}</span>
            <span className="text-xs text-text-secondary">({brand.count})</span>
          </label>
        ))}
      </FilterSection>

      {/* Price Range */}
      <FilterSection title="Price Range" sectionKey="price">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Min Price</label>
            <Input
              type="number"
              placeholder="$0"
              value={localFilters.priceRange?.min || ''}
              onChange={(e) => handlePriceChange('min', e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Max Price</label>
            <Input
              type="number"
              placeholder="$100"
              value={localFilters.priceRange?.max || ''}
              onChange={(e) => handlePriceChange('max', e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </FilterSection>

      {/* Dietary Preferences */}
      <FilterSection title="Dietary Preferences" sectionKey="dietary">
        {dietaryOptions.map((option) => (
          <label key={option.id} className="flex items-center space-x-3 cursor-pointer">
            <Input
              type="checkbox"
              checked={localFilters.dietary?.includes(option.id) || false}
              onChange={(e) => handleFilterChange('dietary', option.id, e.target.checked)}
              className="w-4 h-4"
            />
            <span className="flex-1 text-sm text-text-primary">{option.name}</span>
            <span className="text-xs text-text-secondary">({option.count})</span>
          </label>
        ))}
      </FilterSection>

      {/* Rating */}
      <FilterSection title="Customer Rating" sectionKey="rating">
        {[4, 3, 2, 1].map((rating) => (
          <label key={rating} className="flex items-center space-x-3 cursor-pointer">
            <Input
              type="checkbox"
              checked={localFilters.minRating === rating}
              onChange={() => handleRatingChange(rating)}
              className="w-4 h-4"
            />
            <div className="flex-1">
              {renderRatingStars(rating, localFilters.minRating === rating)}
            </div>
          </label>
        ))}
      </FilterSection>

      {/* Apply Button for Mobile */}
      {isMobile && (
        <div className="pt-4 border-t border-border">
          <Button
            variant="primary"
            onClick={applyFilters}
            fullWidth
          >
            Apply Filters
          </Button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-overlay" onClick={onClose} />
        )}
        <div className={`
          fixed top-0 right-0 h-full w-full sm:w-96 bg-surface shadow-modal z-overlay transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}>
          <div className="h-full overflow-y-auto p-6">
            {content}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6 sticky top-24">
      {content}
    </div>
  );
};

export default FilterPanel;